import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiError, eur } from "@/pages/rc/rcApi";
import OutcomeBadge from "@/pages/rc/OutcomeBadge";
import RCLayout from "@/pages/rc/RCLayout";
import { EligibilityDialog } from "@/pages/rc/EligibilityDialog";
import { detectExcluded } from "@/pages/rc/eligibilityReference";
import { SETTORI, REGIMI, tipiFor, tipoExclusion } from "@/pages/rc/activityTaxonomy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TerminalSquare, Calculator, Save, Check, ChevronsUpDown, Ban } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Dati Ente", "Classificazione Rischio", "Parametri", "Garanzie Opzionali", "Questionario", "Risultato"];

export default function RCNewQuote() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [sp] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);

  const [form, setForm] = useState({
    organization_id: "",
    section_code: "",
    parameter_code: "",
    parameter_value: "",
    massimale: "",
    risk_class: "1",
    sconto_pct: 0,
    aumento_pct: 0,
    maggiorazioni: [],
    rco: { selected: false, abrogazione_franchigia: false },
    organi_sociali: { selected: false, massimale: 25000 },
    tutela_legale: { selected: false, option: "tutti_componenti", massimale: 15000, soci: 0 },
    underwriting_answers: {
      forma_giuridica: "",
      tipologia_ente: "",
      regime_rischio: "Rischio Non Sanitario",
      settore_principale: "",
      tipo_principale: "",
      contratto_annullato_sinistro: false,
      sinistri_in_corso: false,
      premi_arretrati: false,
      rischio_gia_coperto: false,
      benestare_direzione: false,
    },
  });

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/organizations").then((r) => setOrgs(r.data));
  }, []);

  useEffect(() => {
    const ente = sp.get("ente");
    if (ente && !editId) setForm((f) => ({ ...f, organization_id: ente }));
  }, [sp, editId]);

  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then((r) => {
      const p = r.data.input_payload || {};
      const opt = (code) => (p.optional_coverages || []).find((o) => o.coverage_code === code) || {};
      const rco = opt("rco");
      const os = opt("organi_sociali");
      const tl = opt("tutela_legale");
      setForm((f) => ({
        ...f,
        organization_id: p.organization_id || "",
        section_code: p.section_code || "",
        parameter_code: p.parameter_code || "",
        parameter_value: p.parameter_value ?? "",
        massimale: p.massimale ? String(p.massimale) : "",
        risk_class: p.risk_class || "1",
        sconto_pct: p.sconto_pct || 0,
        aumento_pct: p.aumento_pct || 0,
        maggiorazioni: p.maggiorazioni || [],
        rco: { selected: !!rco.selected, abrogazione_franchigia: !!rco.abrogazione_franchigia },
        organi_sociali: { selected: !!os.selected, massimale: os.massimale || 25000 },
        tutela_legale: {
          selected: !!tl.selected, option: tl.option || "tutti_componenti",
          massimale: tl.massimale || 15000, soci: tl.soci || 0,
        },
        underwriting_answers: { ...f.underwriting_answers, ...(p.underwriting_answers || {}) },
      }));
    }).catch(() => toast.error("Impossibile caricare il preventivo da modificare."));
  }, [editId]);

  const section = useMemo(
    () => config?.sections.find((s) => s.code === form.section_code),
    [config, form.section_code]
  );
  const param = useMemo(
    () => section?.parameters.find((p) => p.code === form.parameter_code),
    [section, form.parameter_code]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setUW = (k, v) => setForm((f) => ({ ...f, underwriting_answers: { ...f.underwriting_answers, [k]: v } }));

  const buildPayload = () => ({
    organization_id: form.organization_id || null,
    section_code: form.section_code,
    parameter_code: form.parameter_code,
    parameter_value: Number(form.parameter_value),
    massimale: Number(form.massimale),
    risk_class: form.risk_class,
    sconto_pct: Number(form.sconto_pct) || 0,
    aumento_pct: Number(form.aumento_pct) || 0,
    maggiorazioni: form.maggiorazioni,
    optional_coverages: [
      { coverage_code: "rco", selected: form.rco.selected, abrogazione_franchigia: form.rco.abrogazione_franchigia },
      { coverage_code: "organi_sociali", selected: form.organi_sociali.selected, massimale: form.organi_sociali.massimale },
      {
        coverage_code: "tutela_legale",
        selected: form.tutela_legale.selected,
        option: form.tutela_legale.option,
        massimale: form.tutela_legale.massimale,
        soci: Number(form.tutela_legale.soci) || 0,
      },
    ],
    underwriting_answers: (() => {
      const ua = form.underwriting_answers;
      const ex = tipoExclusion(ua.regime_rischio, ua.settore_principale, ua.tipo_principale, ua.forma_giuridica);
      return { ...ua, tipologia_ente: ua.settore_principale || ua.tipologia_ente, tipo_excluded: !!ex, tipo_excluded_label: ex?.label || "" };
    })(),
  });

  const calcola = async () => {
    if (!form.section_code || !form.parameter_code || !form.parameter_value || !form.massimale) {
      toast.error("Completa sezione, parametro, valore e massimale.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post("/quotes/calculate", buildPayload());
      setResult(data);
      setStep(5);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCalculating(false);
    }
  };

  const save = async (asDefinitive) => {
    try {
      const { data } = await api.post("/quotes", {
        ...buildPayload(),
        quote_id: editId || undefined,
        status: asDefinitive ? "calculated" : "draft",
      });
      toast.success(editId ? "Preventivo aggiornato." : asDefinitive ? "Preventivo salvato." : "Bozza salvata.");
      navigate(`/quotatore-rc/preventivi/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!config) return <RCLayout><div className="text-stone-400 font-mono text-sm">Caricamento configurazione…</div></RCLayout>;

  return (
    <RCLayout>
      <div data-testid="new-quote-page">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900 mb-1">
              {editId ? "Modifica Preventivo" : "Nuovo Preventivo"}
            </h1>
            <p className="text-stone-500 text-sm">
              Versione tariffaria <span className="font-mono">{config.product_version}</span> · {config.product_name}
            </p>
          </div>
          <EligibilityDialog />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Stepper */}
          <div className="lg:col-span-3 lg:sticky lg:top-8">
            <ol className="space-y-1">
              {STEPS.map((label, i) => (
                <li key={label}>
                  <button
                    data-testid={`step-${i}`}
                    onClick={() => i < 5 && setStep(i)}
                    className={`w-full text-left pl-4 py-2 border-l-2 text-sm transition-colors ${
                      step === i
                        ? "border-stone-900 text-stone-900 font-medium"
                        : "border-stone-200 text-stone-400 hover:text-stone-600"
                    }`}
                  >
                    <span className="font-mono text-xs mr-2">{i + 1}</span>
                    {label}
                  </button>
                </li>
              ))}
            </ol>
          </div>

          {/* Form area */}
          <div className="lg:col-span-6 space-y-6">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
              {step === 0 && (
                <div className="space-y-4" data-testid="form-step-ente">
                  <h3 className="font-display text-lg font-medium">Dati Ente</h3>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Ente richiedente</Label>
                    <Popover open={orgOpen} onOpenChange={setOrgOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          data-testid="select-org"
                          className="mt-1.5 w-full justify-between rounded-sm font-normal text-left"
                        >
                          <span className="truncate">
                            {orgs.find((o) => o.id === form.organization_id)?.ragione_sociale || "Cerca e seleziona un ente…"}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                        <Command>
                          <CommandInput placeholder="Digita per cercare (es. Fond...)" data-testid="org-search-input" />
                          <CommandList>
                            <CommandEmpty>Nessun ente trovato.</CommandEmpty>
                            <CommandGroup>
                              {orgs.map((o) => (
                                <CommandItem
                                  key={o.id}
                                  value={o.ragione_sociale}
                                  data-testid={`org-option-${o.id}`}
                                  onSelect={() => { set("organization_id", o.id); setOrgOpen(false); }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${form.organization_id === o.id ? "opacity-100" : "opacity-0"}`} />
                                  <span className="truncate">{o.ragione_sociale}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-stone-400 mt-2">
                      Nessun ente?{" "}
                      <button onClick={() => navigate("/quotatore-rc/enti")} className="underline hover:text-stone-700" data-testid="goto-orgs">
                        Crea anagrafica
                      </button>
                    </p>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4" data-testid="form-step-rischio">
                  <h3 className="font-display text-lg font-medium">Classificazione Rischio</h3>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Sezione di rischio</Label>
                    <Select
                      value={form.section_code}
                      onValueChange={(v) => { set("section_code", v); set("parameter_code", ""); set("massimale", ""); }}
                    >
                      <SelectTrigger data-testid="select-section" className="mt-1.5 rounded-sm">
                        <SelectValue placeholder="Seleziona sezione" />
                      </SelectTrigger>
                      <SelectContent>
                        {config.sections.map((s) => (
                          <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {section?.reserve_direction && (
                      <p className="text-xs text-orange-700 mt-2 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1">
                        Questa sezione è soggetta a valutazione della Direzione.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4" data-testid="form-step-parametri">
                  <h3 className="font-display text-lg font-medium">Parametri di Calcolo</h3>
                  {!section && <p className="text-sm text-stone-400">Seleziona prima una sezione di rischio.</p>}
                  {section && (
                    <>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Parametro tariffario</Label>
                        <Select value={form.parameter_code} onValueChange={(v) => set("parameter_code", v)}>
                          <SelectTrigger data-testid="select-parameter" className="mt-1.5 rounded-sm">
                            <SelectValue placeholder="Seleziona parametro" />
                          </SelectTrigger>
                          <SelectContent>
                            {section.parameters.map((p) => (
                              <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {param && (
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-stone-500">{param.label}</Label>
                          <Input
                            data-testid="input-parameter-value"
                            type="number"
                            value={form.parameter_value}
                            onChange={(e) => set("parameter_value", e.target.value)}
                            className="mt-1.5 rounded-sm"
                            placeholder="Inserisci valore"
                          />
                        </div>
                      )}
                      {form.parameter_code === "tesserati" && (
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-stone-500">Classe di rischio (enti sportivi)</Label>
                          <Select value={form.risk_class} onValueChange={(v) => set("risk_class", v)}>
                            <SelectTrigger data-testid="select-risk-class" className="mt-1.5 rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Classe 1</SelectItem>
                              <SelectItem value="2">Classe 2</SelectItem>
                              <SelectItem value="3">Classe 3 (riservato direzione)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Massimale richiesto</Label>
                        <Select value={String(form.massimale)} onValueChange={(v) => set("massimale", v)}>
                          <SelectTrigger data-testid="select-massimale" className="mt-1.5 rounded-sm">
                            <SelectValue placeholder="Seleziona massimale" />
                          </SelectTrigger>
                          <SelectContent>
                            {section.massimali.map((m) => (
                              <SelectItem key={m} value={String(m)}>{eur(m).replace(",00", "")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="form-step-garanzie">
                  <h3 className="font-display text-lg font-medium">Garanzie Opzionali</h3>

                  <OptRow
                    testid="opt-rco"
                    checked={form.rco.selected}
                    onChange={(v) => set("rco", { ...form.rco, selected: v })}
                    title="RCO – Prestatori di lavoro"
                    desc={param?.has_rco ? "Tariffata sullo stesso parametro della sezione." : "Non disponibile per questo parametro."}
                    disabled={!param?.has_rco}
                  >
                    {form.rco.selected && (
                      <label className="flex items-center gap-2 mt-2 text-sm text-stone-600">
                        <Checkbox
                          data-testid="opt-rco-franchigia"
                          checked={form.rco.abrogazione_franchigia}
                          onCheckedChange={(v) => set("rco", { ...form.rco, abrogazione_franchigia: !!v })}
                        />
                        Abrogazione franchigia RCO (+20%, min € 50)
                      </label>
                    )}
                  </OptRow>

                  <OptRow
                    testid="opt-organi"
                    checked={form.organi_sociali.selected}
                    onChange={(v) => set("organi_sociali", { ...form.organi_sociali, selected: v })}
                    title="RC Organi Sociali"
                    desc="Premio fisso per massimale."
                  >
                    {form.organi_sociali.selected && (
                      <Select
                        value={String(form.organi_sociali.massimale)}
                        onValueChange={(v) => set("organi_sociali", { ...form.organi_sociali, massimale: Number(v) })}
                      >
                        <SelectTrigger data-testid="opt-organi-massimale" className="mt-2 rounded-sm w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[25000, 50000, 100000, 250000].map((m) => (
                            <SelectItem key={m} value={String(m)}>{eur(m).replace(",00", "")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </OptRow>

                  <OptRow
                    testid="opt-tutela"
                    checked={form.tutela_legale.selected}
                    onChange={(v) => set("tutela_legale", { ...form.tutela_legale, selected: v })}
                    title="Tutela Legale"
                    desc="Premio per opzione e numero soci."
                  >
                    {form.tutela_legale.selected && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <Select
                          value={form.tutela_legale.option}
                          onValueChange={(v) => set("tutela_legale", { ...form.tutela_legale, option: v })}
                        >
                          <SelectTrigger data-testid="opt-tutela-option" className="rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {config.tutela_options.map((o) => (
                              <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={String(form.tutela_legale.massimale)}
                          onValueChange={(v) => set("tutela_legale", { ...form.tutela_legale, massimale: Number(v) })}
                        >
                          <SelectTrigger data-testid="opt-tutela-massimale" className="rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[15000, 25000].map((m) => (
                              <SelectItem key={m} value={String(m)}>{eur(m).replace(",00", "")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          data-testid="opt-tutela-soci"
                          type="number"
                          placeholder="N. soci"
                          value={form.tutela_legale.soci}
                          onChange={(e) => set("tutela_legale", { ...form.tutela_legale, soci: e.target.value })}
                          className="rounded-sm col-span-2"
                        />
                      </div>
                    )}
                  </OptRow>

                  <div className="pt-2 border-t border-stone-100">
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Maggiorazioni / Estensioni</Label>
                    <div className="grid grid-cols-1 gap-1.5 mt-2">
                      {config.maggiorazioni.map((m) => (
                        <label key={m.code} className="flex items-center gap-2 text-sm text-stone-600">
                          <Checkbox
                            data-testid={`magg-${m.code}`}
                            checked={form.maggiorazioni.includes(m.code)}
                            onCheckedChange={(v) =>
                              set("maggiorazioni", v ? [...form.maggiorazioni, m.code] : form.maggiorazioni.filter((x) => x !== m.code))
                            }
                          />
                          {m.label} <span className="text-stone-400 font-mono">+{Math.round(m.pct * 100)}%</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-stone-500">Sconto intermediario (max 20%)</Label>
                      <Input
                        data-testid="input-sconto"
                        type="number"
                        min={0}
                        max={20}
                        value={form.sconto_pct}
                        onChange={(e) => set("sconto_pct", e.target.value)}
                        className="mt-1.5 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-stone-500">Aumento di premio (max 300%)</Label>
                      <Input
                        data-testid="input-aumento"
                        type="number"
                        min={0}
                        max={300}
                        value={form.aumento_pct}
                        onChange={(e) => set("aumento_pct", e.target.value)}
                        className="mt-1.5 rounded-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4" data-testid="form-step-questionario">
                  <h3 className="font-display text-lg font-medium">Questionario di Ammissibilità</h3>
                  {(() => {
                    const ua = form.underwriting_answers;
                    const ex = detectExcluded(ua.tipologia_ente, ua.forma_giuridica)
                      || tipoExclusion(ua.regime_rischio, ua.settore_principale, ua.tipo_principale, ua.forma_giuridica);
                    if (!ex) return null;
                    return (
                      <div data-testid="art34-alert" className="flex items-start gap-2.5 bg-rose-50 border border-rose-300 rounded-sm p-3">
                        <Ban className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div className="text-sm text-rose-800">
                          <span className="font-semibold">Rischio escluso (Art. 34).</span>{" "}
                          «{ex.label}»: attività non assicurabile con questa polizza
                          {ex.conditional ? " se non gestita da cooperativa." : "."} Il preventivo risulterà non ammissibile.
                        </div>
                      </div>
                    );
                  })()}
                  <div className="border border-stone-200 rounded-sm p-4 space-y-3 bg-stone-50/50">
                    <p className="text-xs uppercase tracking-widest text-stone-500 font-medium">Attività</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Regime di rischio</Label>
                        <Select
                          value={form.underwriting_answers.regime_rischio || ""}
                          onValueChange={(v) => setForm((f) => ({ ...f, underwriting_answers: { ...f.underwriting_answers, regime_rischio: v, tipo_principale: "" } }))}
                        >
                          <SelectTrigger data-testid="input-regime-rischio" className="mt-1.5 rounded-sm">
                            <SelectValue placeholder="Regime…" />
                          </SelectTrigger>
                          <SelectContent>
                            {REGIMI.map((r) => (
                              <SelectItem key={r} value={r} data-testid={`regime-opt-${r.includes("Non") ? "non-san" : "san"}`}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Settore principale</Label>
                        <Select
                          value={form.underwriting_answers.settore_principale || ""}
                          onValueChange={(v) => setForm((f) => ({ ...f, underwriting_answers: { ...f.underwriting_answers, settore_principale: v, tipo_principale: "" } }))}
                        >
                          <SelectTrigger data-testid="input-settore-principale" className="mt-1.5 rounded-sm">
                            <SelectValue placeholder="Seleziona settore…" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {SETTORI.map((s, si) => (
                              <SelectItem key={s} value={s} data-testid={`settore-opt-${si}`}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Tipo principale</Label>
                        <Select
                          value={form.underwriting_answers.tipo_principale || ""}
                          onValueChange={(v) => {
                            const tipo = tipiFor(form.underwriting_answers.regime_rischio, form.underwriting_answers.settore_principale).find((t) => t.label === v);
                            setForm((f) => ({
                              ...f,
                              risk_class: tipo?.risk_class || f.risk_class,
                              underwriting_answers: { ...f.underwriting_answers, tipo_principale: v },
                            }));
                            if (tipo?.risk_class) toast.info(`Classe di rischio impostata automaticamente: ${tipo.risk_class === "3" ? "RD (riservato direzione)" : tipo.risk_class}`);
                          }}
                          disabled={!form.underwriting_answers.settore_principale}
                        >
                          <SelectTrigger data-testid="input-tipo-principale" className="mt-1.5 rounded-sm">
                            <SelectValue placeholder={form.underwriting_answers.settore_principale ? "Seleziona tipo…" : "Prima il settore"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {tipiFor(form.underwriting_answers.regime_rischio, form.underwriting_answers.settore_principale).map((t, ti) => (
                              <SelectItem key={t.label} value={t.label} data-testid={`tipo-opt-${ti}`}>
                                {t.excluded ? "⛔ " : ""}{t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Forma giuridica</Label>
                    <Input
                      data-testid="input-forma-giuridica"
                      value={form.underwriting_answers.forma_giuridica}
                      onChange={(e) => setUW("forma_giuridica", e.target.value)}
                      className="mt-1.5 rounded-sm max-w-md"
                      placeholder="es. associazione, cooperativa sociale"
                    />
                    <p className="text-xs text-stone-400 mt-1">La classificazione dell'ente è data dal Settore principale qui sopra.</p>
                  </div>
                  <div className="space-y-2 pt-2">
                    {[
                      ["contratto_annullato_sinistro", "Contratto precedente annullato a seguito di sinistro"],
                      ["sinistri_in_corso", "Sostituzione polizza con sinistri in corso"],
                      ["premi_arretrati", "Presenza di premi arretrati"],
                      ["rischio_gia_coperto", "Rischio già coperto da altra compagnia"],
                    ].map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 text-sm text-stone-600">
                        <Checkbox
                          data-testid={`uw-${k}`}
                          checked={form.underwriting_answers[k]}
                          onCheckedChange={(v) => setUW(k, !!v)}
                        />
                        {label}
                      </label>
                    ))}
                    {form.underwriting_answers.rischio_gia_coperto && (
                      <label className="flex items-center gap-2 text-sm text-stone-600 ml-6">
                        <Checkbox
                          data-testid="uw-benestare"
                          checked={form.underwriting_answers.benestare_direzione}
                          onCheckedChange={(v) => setUW("benestare_direzione", !!v)}
                        />
                        Benestare direzione ottenuto
                      </label>
                    )}
                  </div>
                </div>
              )}

              {step === 5 && result && (
                <div className="space-y-4" data-testid="form-step-risultato">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-medium">Risultato</h3>
                    <OutcomeBadge status={result.status} size="lg" />
                  </div>
                  {result.eligibility.blocking_errors.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-sm p-3">
                      {result.eligibility.blocking_errors.map((e, i) => (
                        <p key={`blk-${i}`} className="text-sm text-rose-700">• {e}</p>
                      ))}
                    </div>
                  )}
                  {result.eligibility.warnings.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                      {result.eligibility.warnings.map((e, i) => (
                        <p key={`wrn-${i}`} className="text-sm text-amber-800">• {e}</p>
                      ))}
                    </div>
                  )}
                  {result.premium && <PremiumTable premium={result.premium} />}
                  <div className="flex gap-2 pt-2">
                    <Button data-testid="save-draft-btn" variant="outline" onClick={() => save(false)} className="rounded-sm">
                      <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Salva bozza
                    </Button>
                    <Button data-testid="save-quote-btn" onClick={() => save(true)} className="rounded-sm bg-stone-900 hover:bg-stone-800">
                      Salva preventivo
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <Button
                data-testid="prev-step-btn"
                variant="outline"
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="rounded-sm"
              >
                Indietro
              </Button>
              {step < 4 && (
                <Button data-testid="next-step-btn" onClick={() => setStep((s) => s + 1)} className="rounded-sm bg-stone-900 hover:bg-stone-800">
                  Avanti
                </Button>
              )}
              {step === 4 && (
                <Button data-testid="calculate-quote-btn" onClick={calcola} disabled={calculating} className="rounded-sm bg-stone-900 hover:bg-stone-800">
                  <Calculator className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {calculating ? "Calcolo…" : "Calcola"}
                </Button>
              )}
            </div>
          </div>

          {/* Audit panel */}
          <div className="lg:col-span-3 lg:sticky lg:top-8">
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-4 min-h-[300px]">
              <div className="flex items-center gap-2 mb-3 text-stone-500">
                <TerminalSquare className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-widest">Spiegazione del calcolo</span>
              </div>
              {!result && <p className="text-xs text-stone-400 audit-console">Nessun calcolo eseguito. Premi “Calcola”.</p>}
              {result && (
                <div className="audit-console text-stone-700 space-y-2" data-testid="audit-panel">
                  <div className="text-stone-400">› versione {result.audit_summary.product_version}</div>
                  {result.audit_summary.rules_applied.map((r, i) => (
                    <div key={`${r.rule}-${i}`} className="border-l-2 border-stone-300 pl-2">
                      <div className="text-stone-500">[{r.rule}] {r.label || r.msg}</div>
                      {r.formula && <div className="text-stone-800">{r.formula}</div>}
                      {r.bracket && <div className="text-stone-400">scaglione {r.bracket} · mass. {r.massimale}</div>}
                    </div>
                  ))}
                  {result.premium && (
                    <div className="border-t border-stone-300 pt-2 mt-2 font-medium">
                      LORDO TOTALE = {eur(result.premium.gross_total)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </RCLayout>
  );
}

function OptRow({ testid, checked, onChange, title, desc, disabled, children }) {
  return (
    <div className={`border border-stone-200 rounded-sm p-3 ${disabled ? "opacity-50" : ""}`}>
      <label className="flex items-start gap-2.5">
        <Checkbox data-testid={testid} checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
        <span>
          <span className="text-sm font-medium text-stone-800 block">{title}</span>
          <span className="text-xs text-stone-400">{desc}</span>
        </span>
      </label>
      {children}
    </div>
  );
}

function PremiumTable({ premium }) {
  const labels = { rct: "RCT", rco: "RCO", organi_sociali: "Organi Sociali", tutela_legale: "Tutela Legale" };
  return (
    <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid="premium-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-100 text-stone-500 text-xs uppercase tracking-wide">
            <th className="text-left font-medium px-3 py-2">Garanzia</th>
            <th className="text-right font-medium px-3 py-2">Imponibile</th>
            <th className="text-right font-medium px-3 py-2">Imposte</th>
            <th className="text-right font-medium px-3 py-2">Lordo</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {Object.entries(premium.breakdown).map(([k, v]) => (
            <tr key={k} className="border-b border-stone-100">
              <td className="px-3 py-2 font-sans text-stone-700">{labels[k] || k}</td>
              <td className="px-3 py-2 text-right">{eur(v.net)}</td>
              <td className="px-3 py-2 text-right text-stone-400">{eur(v.tax)}</td>
              <td className="px-3 py-2 text-right">{eur(v.gross)}</td>
            </tr>
          ))}
          <tr className="bg-stone-50 font-semibold">
            <td className="px-3 py-2 font-sans">TOTALE</td>
            <td className="px-3 py-2 text-right">{eur(premium.net_total)}</td>
            <td className="px-3 py-2 text-right text-stone-400">{eur(premium.tax_total)}</td>
            <td className="px-3 py-2 text-right" data-testid="premium-gross-total">{eur(premium.gross_total)}</td>
          </tr>
        </tbody>
      </table>
      {premium.minimum_premium_applied && (
        <div data-testid="min-premium-badge" className="px-3 py-2.5 bg-amber-100 border-t border-amber-300 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-900">Premio Minimo da Tariffa applicato</span>
          <span className="text-xs text-amber-800 ml-auto font-mono">{eur(premium.minimum_premium_value)}</span>
        </div>
      )}
    </div>
  );
}
